import React, { useCallback, useEffect, useState } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { theme } from './src/shared/theme/theme';
import { Ionicons } from '@expo/vector-icons';
import { AuthProvider, useAuth } from './src/infrastructure/auth/AuthContext';
import { AuthScreen } from './src/interfaces/screens/AuthScreen';
import { QuickOrderScreen } from './src/interfaces/screens/QuickOrderScreen';
import { OrderHistoryScreen } from './src/interfaces/screens/OrderHistoryScreen';
import { ProfileScreen } from './src/interfaces/screens/ProfileScreen';
import { HomeeJobsScreen } from './src/interfaces/screens/HomeeJobsScreen';
import { HomeeVerificationScreen } from './src/interfaces/screens/HomeeVerificationScreen';
import { FounderDashboard } from './src/interfaces/screens/FounderDashboard';
import { ProfileRepository, Profile } from './src/infrastructure/repositories/ProfileRepository';

const Tab = createBottomTabNavigator();

// Founder-only access to platform-wide KPIs — matches CLOUDFLARE/Supabase RLS founder policy.
const FOUNDER_EMAIL = 'hlancaric@gmail.com';

const AppShell = () => {
  const { session, loading } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const loadProfile = useCallback(async () => {
    if (!session) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    const p = await ProfileRepository.getOwnProfile(session.user.id);
    setProfile(p);
    setProfileLoading(false);
  }, [session]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (loading || (session && profileLoading)) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: theme.colors.background }}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  if (!session) {
    return <AuthScreen />;
  }

  const isFounder = session.user.email === FOUNDER_EMAIL;

  if (profile?.role === 'HOMEE' && !profile.is_verified) {
    return <HomeeVerificationScreen onVerified={loadProfile} />;
  }

  return (
    <NavigationContainer>
      <Tab.Navigator
        initialRouteName={profile?.role === 'HOMEE' ? 'Zakázky' : 'Objednat'}
        screenOptions={{
          headerShown: false,
          tabBarActiveTintColor: theme.colors.accent,
          tabBarInactiveTintColor: theme.colors.text.muted,
          tabBarStyle: {
            backgroundColor: '#FFFFFF',
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
            height: 65,
            paddingBottom: 10,
          },
        }}
      >
        {profile?.role === 'HOMEE' ? (
          <Tab.Screen
            name="Zakázky"
            component={HomeeJobsScreen}
            options={{ tabBarIcon: ({ color, size }) => <Ionicons name="briefcase" size={size} color={color} /> }}
          />
        ) : (
          <>
            <Tab.Screen
              name="Objednat"
              component={QuickOrderScreen}
              options={{ tabBarIcon: ({ color, size }) => <Ionicons name="add-circle" size={size} color={color} /> }}
            />
            <Tab.Screen
              name="Historie"
              component={OrderHistoryScreen}
              options={{ tabBarIcon: ({ color, size }) => <Ionicons name="time" size={size} color={color} /> }}
            />
          </>
        )}
        <Tab.Screen
          name="Profil"
          component={ProfileScreen}
          options={{ tabBarIcon: ({ color, size }) => <Ionicons name="person" size={size} color={color} /> }}
        />
        {isFounder && (
          <Tab.Screen
            name="Founder"
            component={FounderDashboard}
            options={{ tabBarIcon: ({ color, size }) => <Ionicons name="analytics" size={size} color={color} /> }}
          />
        )}
      </Tab.Navigator>
    </NavigationContainer>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
